import {useRef} from 'react';
import {message} from 'antd';
import {useQueryClient} from '@tanstack/react-query';
import {uploadService} from '../services/upload';
import {directoryService} from '../services/directory';
import {calcFileMd5} from '../utils/md5';
import {convertToMp4, getObsKeyFromSignedUrl} from '../utils/file';
import {getExtFromName, getPreviewType} from '../utils/filePreview';
import {uploadFileToObsOnly, uploadFilesToObs} from '../services/upload/uploadObs';
import type {FileProgressItem, FolderUploadedObsFile, UploadedObsFile, UploadOption, UploadStatus} from '../types/upload';
import {useUploadStore} from '../store/uploadStore';

export function useUpload(parentId: number | string) {
  const queryClient = useQueryClient();
  const {progress, setProgress, fileProgressList, setFileProgressList, updateFileProgressList} = useUploadStore();

  const cancelRef = useRef(false);

  function cancelUpload() {
    cancelRef.current = true;
  }

  function resetCancelUpload() {
    cancelRef.current = false;
  }

  function initFileProgress(files: File[], initialStatus: UploadStatus = 'waiting') {
    setFileProgressList(files.map(file => ({
      uid: (file as any).uid || `${file.name}-${file.size}-${file.lastModified}`,
      name: file.name,
      size: file.size,
      percent: 0,
      status: initialStatus,
    })));
  }

  function updateFileProgress(file: File, patch: Partial<FileProgressItem>) {
    const uid = (file as any).uid ||
      `${file.name}-${file.size}-${file.lastModified}`;

    updateFileProgressList(prev =>
      prev.map(item =>
        item.uid === uid
          ? {...item, ...patch}
          : item
      ));
  }

  async function upload(file: File, options?: UploadOption) {
    resetCancelUpload();
    initFileProgress([file], 'checking');
    setProgress(0);

    try {
      const md5 = await calcFileMd5(file);

      const checkRes = await uploadService.checkFile({
        file_name: file.name,
        directory_id: parentId,
        file_md5: md5,
      });

      const isExist = checkRes && typeof checkRes === 'number';

      if (isExist) {
        message.success('文件已存在, 秒传成功');

        setProgress(100);
        options?.onProgress?.(100);

        await refreshAfterUpload();

        updateFileProgress(file, {percent: 100, status: 'success'});

        return {
          checkRes,
          isInstantUpload: true,
        };
      }

      updateFileProgress(file, {status: 'uploading'});

      const uploaded = await uploadFilesToObs(file, {
        ...options,
        onProgress: percent => {
          setProgress(percent);
          updateFileProgress(file, {percent, status: 'uploading'});
          options?.onProgress?.(percent);
        }
      });

      const obsKey = getObsKeyFromSignedUrl(uploaded.obs_key);
      const extension = getExtFromName(file.name);
      // TODO 如果是视频格式需要后端支持返回 transcoded_obskey 之类的转换为 mp4 后的 obskey，以支持但文件上传后的直接弹出 FileMetaModal 预览
      const finalObsKey = getPreviewType(extension) === 'video' ? convertToMp4(obsKey) : obsKey;

      const completeRes = await uploadService.completeUpload({
        obskey: obsKey,
        file_name: file.name,
        directory_id: parentId,
        custom_tags: options?.tags || [],
        visibility: options?.permission || 'edit',
      });

      await refreshAfterUpload();

      updateFileProgress(file, {percent: 100, status: 'success'});
      setProgress(100);
      message.success('上传成功');

      return {
        completeRes: {
          ...completeRes,
          obskey: finalObsKey,
          extension
        },
        isInstantUpload: false,
      };
    } catch (e) {
      console.error(e);
      updateFileProgress(file, {percent: 0, status: 'error'});
      message.error('上传失败');
      throw e;
    }
  }

  async function uploadMany(files: File[], options?: UploadOption) {
    if (!files.length) return;

    resetCancelUpload();
    initFileProgress(files);
    setProgress(0);

    const failedFiles: File[] = [];

    try {
      const uploadedFiles: UploadedObsFile[] = [];

      for (let i = 0; i < files.length; i++) {
        if (cancelRef.current) {
          break;
        }

        const file = files[i];

        try {
          updateFileProgress(file, {status: 'checking'});

          const md5 = await calcFileMd5(file);

          // md5 校验重复接口
          const checkRes = await uploadService.checkFileByMd5({
            file_md5: md5,
          });

          let obsKey = '';

          if (checkRes && typeof checkRes === 'string') {
            obsKey = checkRes;

            updateFileProgress(file, {percent: 100, status: 'success'});
          } else {
            updateFileProgress(file, {status: 'uploading'});

            const uploaded = await uploadFilesToObs(file, {
              ...options,
              onProgress: percent => {
                updateFileProgress(file, {percent, status: 'uploading'});
              }
            });

            obsKey = getObsKeyFromSignedUrl(uploaded.obs_key);

            updateFileProgress(file, {percent: 100, status: 'success'});
          }

          uploadedFiles.push({
            obs_key: obsKey,
            file_name: file.name,
            file_md5: md5,
          });

          const totalPercent = Math.floor(((i + 1) / files.length) * 100);
          setProgress(totalPercent);
          options?.onProgress?.(totalPercent);
        } catch (e) {
          failedFiles.push(file);
          updateFileProgress(file, {percent: 0, status: 'error'});
          console.log(`${file.name} 上传失败`, e);
        }
      }

      const cancelled = cancelRef.current;

      if (!uploadedFiles.length) {
        if (cancelled) {
          return {
            cancelled: true,
            failedFiles,
            successFiles: []
          };
        }

        message.error('全部文件上传失败');
        return;
      }

      const res = await uploadService.completeBatchUpload({
        directory_id: parentId,
        visibility: options?.permission || 'edit',
        files: uploadedFiles
      });

      await refreshAfterUpload();

      setProgress(100);

      if (failedFiles.length) {
        message.success(`成功上传 ${uploadedFiles.length} 个文件，失败 ${failedFiles.length}}`);
      } else {
        message.success('上传成功');
      }

      return {
        res,
        failedFiles,
        successFiles: uploadedFiles,
        cancelled
      };
    } catch (e) {
      console.error(e);
      message.error('批量上传失败');
      throw e;
    }
  }

  async function uploadFolder(files: File[], options?: UploadOption) {
    if (!files.length) return;

    resetCancelUpload();
    initFileProgress(files);
    setProgress(0);

    try {
      const directoryMap = new Map<string, number | string>();
      const uploadedFiles: FolderUploadedObsFile[] = [];
      const failedFiles: File[] = [];

      // 1.创建目录结构
      for (const file of files) {
        if (cancelRef.current) {
          return;
        }

        const relativePath = (file as any).webkitRelativePath || file.name;
        const parts = relativePath.split('/').filter(Boolean);

        if (parts.length <= 1) continue;

        const folderParts = parts.slice(0, -1);

        let parentDirectoryId = parentId;
        let currentPath = '';

        for (const folderName of folderParts) {
          currentPath = currentPath ? `${currentPath}/${folderName}` : folderName;

          if (directoryMap.has(currentPath)) {
            parentDirectoryId = directoryMap.get(currentPath)!;
            continue;
          }

          const newDirectoryId = await directoryService.create({
            parent_id: parentDirectoryId,
            name: folderName,
          });

          if (!newDirectoryId) {
            message.error(`创建文件夹失败：${folderName}`);
            return;
          }

          directoryMap.set(currentPath, newDirectoryId);
          parentDirectoryId = newDirectoryId;
        }
      }

      // 2.按目录上传文件
      for (let i = 0; i < files.length; i++) {
        if (cancelRef.current) {
          break;
        }

        const file = files[i];

        try {
          const relativePath = (file as any).webkitRelativePath || file.name;
          const parts = relativePath.split('/').filter(Boolean);

          const fileName = parts[parts.length - 1];
          const folderPath = parts.slice(0, -1).join('/');

          const targetDirectoryId = folderPath
            ? directoryMap.get(folderPath) || parentId
            : parentId;

          const realFile = new File([file], fileName, {
            type: file.type,
          });

          updateFileProgress(file, {status: 'checking'});

          const md5 = await calcFileMd5(realFile);

          const checkRes = await uploadService.checkFileByMd5({
            file_md5: md5,
          });

          let obsKey = '';

          if (checkRes && typeof checkRes === 'string') {
            obsKey = checkRes;
          } else {
            const uploaded = await uploadFileToObsOnly(realFile, {
              ...options,
              onProgress: (percent: any) => {
                updateFileProgress(file, {percent, status: 'uploading'});
              }
            });

            obsKey = getObsKeyFromSignedUrl(uploaded.obs_key);
          }

          uploadedFiles.push({
            obs_key: obsKey,
            file_name: fileName,
            file_md5: md5,
            directory_id: targetDirectoryId,
          });

          updateFileProgress(file, {percent: 100, status: 'success'});
        } catch (e) {
          failedFiles.push(file);
          updateFileProgress(file, {percent: 0, status: 'error'});

          console.log(`${file.name} 上传失败`, e);
        }

        const totalPercent = Math.floor(((i + 1) / files.length) * 100);
        setProgress(totalPercent);
        options?.onProgress?.(totalPercent);
      }

      const cancelled = cancelRef.current;

      if (!uploadedFiles.length) {
        if (cancelled) {
          return {
            cancelled: true,
            failedFiles,
            successFiles: []
          };
        }

        message.error('文件夹内文件全部上传失败');
        return;
      }

      const groupMap = new Map<number | string, UploadedObsFile[]>();

      uploadedFiles.forEach(file => {
        const list = groupMap.get(file.directory_id) || [];

        list.push({
          obs_key: file.obs_key,
          file_name: file.file_name,
          file_md5: file.file_md5,
        });

        groupMap.set(file.directory_id, list);
      });

      const groupKeys = Array.from(groupMap.keys());

      for (let i = 0; i < groupKeys.length; i++) {
        const directoryId = groupKeys[i];

        await uploadService.completeBatchUpload({
          directory_id: directoryId,
          visibility: options?.permission || 'edit',
          files: groupMap.get(directoryId) || [],
        });
      }

      await refreshAfterUpload();

      setProgress(100);
      if (failedFiles.length) {
        message.warning(`文件夹部分成功上传成功，成功 ${uploadedFiles.length} 个，失败 ${failedFiles.length}} 个`);
      } else {
        message.success('文件夹上传成功');
      }

      return {
        failedFiles,
        successFiles: uploadedFiles,
        cancelled
      };
    } catch (e) {
      console.error(e);
      message.error('文件夹上传失败');
      throw e;
    }
  }

  async function refreshAfterUpload() {
    await queryClient.invalidateQueries({
      queryKey: ['file-list'],
    });
  }

  function resetUploadState() {
    setProgress(0);
    setFileProgressList([]);
  }

  return {
    upload,
    uploadMany,
    uploadFolder,
    cancelUpload,
    progress,
    uploading: fileProgressList.some(item => item.status === 'uploading' || item.status === 'checking'),
    fileProgressList,
    resetUploadState
  };
}